// /frontend/src/components/PrecoroQBCustomers.js
import React, { useState, useMemo } from 'react';
import { Modal, Button, Table, Typography, List, Spin, Input, Select, Tooltip, Alert, Collapse } from 'antd';
import { ArrowLeftOutlined, SearchOutlined, SyncOutlined, SwapOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { US, CA } from 'country-flag-icons/react/3x2';
import { syncPrecoroQBCustomers, autoMatchQBCustomers, updateSiteQBCustomerMapping } from '../api';
import QBCustomerSelect from './QBCustomerSelect';

// Wrapper component for QBCustomerSelect to handle site-specific logic
const QBCustomerSelectForSite = ({ site, qbOptions, onMapping }) => {
  if (site.country === 'Unknown') {
    return <span style={{ color: '#888' }}>Unknown country</span>;
  }

  const options = site.country === 'Canada' ? qbOptions.CA : qbOptions.US;
  
  return (
    <div className="qb-customer-cell" style={{ 
      display: 'flex', 
      gap: '8px', 
      alignItems: 'center', 
      paddingTop: '4px', 
      paddingBottom: '4px', 
      width: '100%',
      overflow: 'visible',
      minHeight: 'fit-content'
    }}>
      <div style={{ 
        flex: 1, 
        minHeight: 'fit-content',
        overflow: 'visible',
        position: 'relative'
      }}>
        <QBCustomerSelect
          hierarchicalOptions={options.hierarchicalOptions}
          optionMap={options.optionMap}
          value={site.precoro_qb_customer_option_id}
          onChange={(value) => onMapping(site.site_id, value)}
          placeholder="Select QB customer"
        />
      </div>
    </div>
  );
};

const PrecoroQBCustomers = ({
  open,
  onClose,
  sites,
  onSiteUpdate,
  onQBCustomersSync,
  messageApi,
  qbOptionsCA,
  qbOptionsUS
}) => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');
  const [mappingFilter, setMappingFilter] = useState('all');
  
  const [syncResponse, setSyncResponse] = useState(null);
  const [matchResponse, setMatchResponse] = useState(null);

  // Use the options passed from parent
  const qbOptions = useMemo(() => ({
    CA: qbOptionsCA,
    US: qbOptionsUS
  }), [qbOptionsCA, qbOptionsUS]);

  // Filter sites based on search, country, and mapping status
  const filteredSites = useMemo(() => {
    let result = sites || [];
    
    // Multi-term search filter
    if (searchQuery) {
      const searchTerms = searchQuery.toLowerCase().trim().split(/\s+/);
      result = result.filter(site => {
        const searchableText = [
          site.site_name,
          site.city,
          site.client_name
        ].filter(Boolean).join(' ').toLowerCase();
        
        // All search terms must be present
        return searchTerms.every(term => searchableText.includes(term));
      });
    }
    
    // Country filter
    if (countryFilter !== 'all') {
      result = result.filter(site => site.country === countryFilter);
    }
    
    // Mapping status filter
    if (mappingFilter === 'mapped') {
      result = result.filter(site => site.precoro_qb_customer_option_id != null);
    } else if (mappingFilter === 'unmapped') {
      result = result.filter(site => site.precoro_qb_customer_option_id == null);
    }
    
    return result;
  }, [sites, searchQuery, countryFilter, mappingFilter]);

  const syncQBCustomers = async () => {
    setLoading(true);
    setSyncing(true);
    try {
      const response = await syncPrecoroQBCustomers();
      setSyncResponse(response);
      
      // Trigger parent to reload QB customers (not sites)
      if (onQBCustomersSync) {
        onQBCustomersSync();
      }
      
      messageApi.success('QuickBooks customers synced successfully');
    } catch (error) {
      console.error('Error syncing QB customers:', error);
      messageApi.error('Failed to sync QuickBooks customers');
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  };

  const matchSites = async () => {
    setLoading(true);
    try {
      const response = await autoMatchQBCustomers();
      setMatchResponse(response);
      
      // Update each matched site individually instead of reloading all sites
      if (onSiteUpdate && response.matched) {
        response.matched.forEach(match => {
          if (match.site) {
            onSiteUpdate(match.site);
          }
        });
      }
      
      const matchedCount = response.matched?.length || 0;
      messageApi.success(`Auto-matched ${matchedCount} site(s) to QuickBooks customers`);
    } catch (error) {
      console.error('Error auto-matching sites:', error);
      messageApi.error('Failed to auto-match sites');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setSyncResponse(null);
    setMatchResponse(null);
  };

  const handleSiteMapping = async (siteId, qbCustomerOptionId) => {
    try {
      const updatedSite = await updateSiteQBCustomerMapping(siteId, qbCustomerOptionId);
      
      // Update the specific site in parent's list
      if (onSiteUpdate && updatedSite) {
        onSiteUpdate(updatedSite);
      }
      
      messageApi.success('Site mapping updated');
    } catch (error) {
      console.error('Error updating site mapping:', error);
      messageApi.error('Failed to update site mapping');
    }
  };

  const qbTableComponents = useMemo(() => ({
    body: {
      row: ({ className, style, ...restProps }) => (
        <tr
          className={className}
          style={{ ...style, height: 'auto', overflow: 'visible' }}
          {...restProps}
        />
      ),
      cell: ({ style, ...restProps }) => (
        <td
          style={{
            ...style,
            paddingTop: 16,
            paddingBottom: 16,
            whiteSpace: 'normal',
            verticalAlign: 'middle',
            height: 'auto',
            overflow: 'visible'
          }}
          {...restProps}
        />
      )
    }
  }), []);

  const columns = [
    {
      title: 'Site Name',
      dataIndex: 'site_name',
      key: 'site_name',
      width: '25%',
      sorter: (a, b) => a.site_name.localeCompare(b.site_name),
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Client',
      dataIndex: 'client_name',
      key: 'client_name',
      width: '20%',
      sorter: (a, b) => (a.client_name || '').localeCompare(b.client_name || ''),
    },
    {
      title: 'City',
      dataIndex: 'city',
      key: 'city',
      width: '10%',
      sorter: (a, b) => (a.city || '').localeCompare(b.city || ''),
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      align: 'center',
      width: '10%',
      sorter: (a, b) => a.country.localeCompare(b.country),
      render: (country) => {
        if (country === 'Canada') {
          return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <CA title="Canada" style={{ width: '24px', height: '16px' }} />
            </div>
          );
        } else if (country === 'USA') {
          return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <US title="United States" style={{ width: '24px', height: '16px' }} />
            </div>
          );
        } else {
          return 'Unknown';
        }
      },
    },
    {
      title: 'QB Customer Mapping',
      key: 'mapping',
      width: '25%',
      render: (_, site) => (
        <QBCustomerSelectForSite 
          site={site} 
          qbOptions={qbOptions} 
          onMapping={handleSiteMapping}
        />
      ),
    },
    {
      title: 'Status',
      key: 'status',
      align: 'center',
      width: '10%',
      render: (_, site) => {
        if (site.country === 'Unknown') {
          return <span style={{ color: '#faad14' }}>⚠️ No Country</span>;
        }
        return site.precoro_qb_customer_option_id ? (
          <span style={{ color: 'green', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
            <CheckOutlined style={{ fontSize: '18px' }} />
            Mapped
          </span>
        ) : (
          <span style={{ color: 'red', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
            <CloseOutlined style={{ fontSize: '18px' }} />
            Unmapped
          </span>
        );
      },
    },
  ];

  const renderSyncSummary = () => {
    if (!syncResponse) return null;

    const renderItemList = (items, title) => {
      if (!items || items.length === 0) return null;

      // Build tree structure using IDs
      const itemMap = {};
      const roots = [];

      // First pass: create map of all items by ID
      items.forEach(item => {
        itemMap[item.id] = { ...item, children: [] };
      });

      // Second pass: build parent-child relationships
      items.forEach(item => {
        if (!item.parent_id) {
          // Root item (no parent)
          roots.push(itemMap[item.id]);
        } else {
          // Child item - add to parent's children array
          const parent = itemMap[item.parent_id];
          if (parent) {
            parent.children.push(itemMap[item.id]);
          } else {
            // Parent not in this list, treat as root
            roots.push(itemMap[item.id]);
          }
        }
      });

      // Flatten tree into array with depth info for rendering
      const flattenTree = (item, depth = 0) => {
        const result = [{ ...item, depth }];
        if (item.children && item.children.length > 0) {
          item.children.forEach(child => {
            result.push(...flattenTree(child, depth + 1));
          });
        }
        return result;
      };

      // Sort roots and their children
      roots.sort((a, b) => a.name.localeCompare(b.name));
      
      // Recursively sort all children
      const sortChildren = (item) => {
        if (item.children && item.children.length > 0) {
          item.children.sort((a, b) => a.name.localeCompare(b.name));
          item.children.forEach(sortChildren);
        }
      };
      roots.forEach(sortChildren);

      // Flatten the tree for List rendering
      const flatItems = roots.flatMap(root => flattenTree(root));

      return (
        <Collapse 
          size="small"
          items={[{
            key: title,
            label: (
              <span>
                <Typography.Text strong>{title}: </Typography.Text>
                <Typography.Text type="secondary">({items.length})</Typography.Text>
              </span>
            ),
            children: (
              <List
                size="small"
                bordered
                dataSource={flatItems}
                renderItem={item => (
                  <List.Item style={{ 
                    paddingLeft: `${16 + item.depth * 24}px`,
                    paddingTop: '8px',
                    paddingBottom: '8px'
                  }}>
                    <Typography.Text>
                      {item.depth > 0 && (
                        <span style={{ color: '#bfbfbf', marginRight: '8px' }}>└─</span>
                      )}
                      {item.name}
                    </Typography.Text>
                  </List.Item>
                )}
              />
            )
          }]}
          style={{ marginBottom: '16px' }}
        />
      );
    };

    return (
      <div style={{ marginTop: '20px' }}>
        {['CANADA', 'USA'].map(country => {
          const data = syncResponse[country];
          if (!data) return null;

          return (
            <div key={country} style={{ marginBottom: '30px' }}>
              <Typography.Title level={5}>{country}</Typography.Title>
              {data.error ? (
                <Alert message={data.error} type="error" />
              ) : (
                <div>
                  <Alert 
                    message={`Successfully synced ${data.total} QB customer options`} 
                    type="success" 
                    showIcon
                    style={{ marginBottom: '16px' }}
                  />
                  {renderItemList(data.created, 'Created')}
                  {renderItemList(data.updated, 'Updated')}
                  {renderItemList(data.deactivated, 'Deactivated')}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderMatchSummary = () => {
    if (!matchResponse) return null;

    return (
      <>
        <Alert
          message={`Successfully matched ${matchResponse.matched?.length || 0} site(s)`}
          type="success"
          style={{ marginBottom: '16px' }}
        />
        
        {matchResponse.matched?.length > 0 && (
          <>
            <Typography.Title level={5}>Matched Sites</Typography.Title>
            <List
              dataSource={matchResponse.matched}
              renderItem={item => (
                <List.Item>
                  <Typography.Text strong>{item.site?.site_name}</Typography.Text>
                  {' → '}
                  <Typography.Text>{item.site?.precoro_qb_customer?.name}</Typography.Text>
                </List.Item>
              )}
            />
          </>
        )}

        {matchResponse.skipped_ambiguous?.length > 0 && (
          <>
            <Typography.Title level={5} style={{ marginTop: '16px', color: '#faad14' }}>
              Skipped (Ambiguous Matches)
            </Typography.Title>
            <List
              dataSource={matchResponse.skipped_ambiguous}
              renderItem={item => (
                <List.Item>
                  <Typography.Text>{item.site_name}</Typography.Text>
                  {' - '}
                  <Typography.Text type="secondary">{item.reason}</Typography.Text>
                </List.Item>
              )}
            />
          </>
        )}

        {matchResponse.skipped_no_match?.length > 0 && (
          <>
            <Typography.Title level={5} style={{ marginTop: '16px' }}>
              Skipped (No Match Found)
            </Typography.Title>
            <Typography.Text type="secondary">
              {matchResponse.skipped_no_match.length} site(s) had no matching QB customer
            </Typography.Text>
          </>
        )}
      </>
    );
  };

  return (
    <Modal
      open={open}
      title="Map Sites to QuickBooks Customers"
      onCancel={onClose}
      style={{ top: '10vh' }}
      styles={{ body: { maxHeight: '75vh', overflow: (syncResponse || matchResponse) ? 'auto' : 'hidden' } }}
      footer={null}
      width={'90%'}
    >
      {loading ? (
        <Spin />
      ) : syncResponse ? (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <div style={{ width: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center' }}>
            <Button
              shape='circle'
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
            />
          </div>
          <div style={{ flex: 1, marginLeft: '16px' }}>
            <Typography.Title level={4} style={{ marginTop: 0 }}>Sync Results</Typography.Title>
            {renderSyncSummary()}
          </div>
        </div>
      ) : matchResponse ? (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <div style={{ width: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center' }}>
            <Button
              shape='circle'
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
            />
          </div>
          <div style={{ flex: 1, marginLeft: '16px' }}>
            <Typography.Title level={4} style={{ marginTop: 0 }}>Auto-Match Results</Typography.Title>
            {renderMatchSummary()}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingBottom: '24px' }}>
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: '16px' }}>
            <Input
              placeholder="Search sites"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              style={{ width: '300px' }}
              prefix={<SearchOutlined style={{ color: '#d9d9d9' }} />}
            />
            
            <div style={{ display: 'flex', gap: '8px' }}>
              <Select
                value={countryFilter}
                onChange={setCountryFilter}
                style={{ width: 'auto' }}
                options={[
                  { value: 'all', label: 'All Countries' },
                  { value: 'Canada', label: 'Canada' },
                  { value: 'USA', label: 'USA' },
                ]}
              />
              
              <Select
                value={mappingFilter}
                onChange={setMappingFilter}
                style={{ width: '120px' }}
                options={[
                  { value: 'all', label: 'All Sites' },
                  { value: 'mapped', label: 'Mapped' },
                  { value: 'unmapped', label: 'Unmapped' },
                ]}
              />
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
              <Tooltip
                title={
                  <div style={{ textAlign: 'center'}}>
                    <p>Auto-match sites to QB customers by exact name.</p>
                    <p>(Name must match exactly, case-insensitive.)</p>
                  </div>
                }
              >
                <Button
                  onClick={matchSites}
                  loading={loading}
                >
                  Auto-Match Sites <SwapOutlined />
                </Button>
              </Tooltip>
              
              <Tooltip title="Sync QuickBooks customers from Precoro (both Canada and USA)">
                <Button
                  onClick={syncQBCustomers}
                  loading={loading}
                >
                  Sync from Precoro <SyncOutlined />
                </Button>
              </Tooltip>
            </div>
          </div>
          
          <Alert
            message="QuickBooks Customer Mapping"
            description="Map each site to a QuickBooks customer in Precoro. This mapping will be used when posting Purchase Orders to ensure the correct customer is assigned to salting PO line items."
            type="info"
            showIcon
          />
          
          <Table
            dataSource={filteredSites}
            columns={columns}
            loading={loading || syncing}
            rowKey="site_id"
            pagination={false}
            scroll={{ y: 'calc(75vh - 200px)', x: 'max-content' }}
            sticky
            className="qb-customer-table"
            rowClassName={() => 'qb-customer-row'}
            onRow={() => ({
              style: { height: 'auto' }
            })}
            components={qbTableComponents}
            tableLayout="auto"
          />
        </div>
      )}
    </Modal>
  );
};

export default PrecoroQBCustomers;
